/**
 * Route-level admin guard. Renders its children only for superusers; any other
 * authenticated user gets a clean access-denied state instead of the page.
 *
 * The frontend's only admin signal is `user.is_superuser` (the JWT carries no
 * granular permissions), and the admin-only pages (Users, Roles) are gated
 * server-side by `*:*`, which is exactly the superuser permission. Without this
 * guard a non-admin reaching /users or /roles directly hits the page's `*:*`
 * 403 and sees the generic "Something went wrong" error card (#353).
 *
 * Mounted inside <Layout> so the surrounding nav stays visible and the user can
 * navigate away.
 */

import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { AccessDeniedState } from '@/components/EmptyState';

interface RequireAdminProps {
  children: React.ReactNode;
}

export const RequireAdmin: React.FC<RequireAdminProps> = ({ children }) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  if (!user?.is_superuser) {
    return <AccessDeniedState onBack={() => navigate('/dashboard')} />;
  }

  return <>{children}</>;
};
